import type { ServicePageData } from "$lib/types/service-detail";

const colorCorrectionMedia = {
  heroFashionTop1: {
    src: "/images/color-correction/color-correction-hero-fashion-top-palette.webp",
    alt: "Fashion model wearing a color-corrected and palette-matched top",
    width: 1600,
    height: 2000,
  },
  heroFashionTop2: {
    src: "/images/color-correction/color-correction-hero-fashion-top-detail.webp",
    alt: "Fashion model top color-calibrated view",
    width: 1600,
    height: 2000,
  },
  heroSunglasses: {
    src: "/images/color-correction/color-correction-hero-designer-sunglasses-front-view.webp",
    alt: "Designer sunglasses front view color-calibrated and retouched",
    width: 1600,
    height: 2000,
  },
  introFashionBoard: {
    src: "/videos/color-correction-intro-clothing-color-change.mp4",
    alt: "Fashion model clothing colorway shifts and swatch matching workflow preview video",
    width: 1080,
    height: 1350,
  },
  comparisonOriginal: {
    src: "/images/color-correction/color-correction-comparison-cosmetic-cream-before.webp",
    alt: "Cosmetic cream product before color correction",
    width: 1600,
    height: 2000,
  },
  comparisonCorrected: {
    src: "/images/color-correction/color-correction-comparison-cosmetic-cream-after.webp",
    alt: "Cosmetic cream product after color correction",
    width: 1600,
    height: 2000,
  },
  patternChangeBefore: {
    src: "/images/color-correction/color-correction-showcase-pattern-change-dress-before.webp",
    alt: "Blue patterned dress before the pattern change",
    width: 400,
    height: 500,
  },
  patternChangeAfter: {
    src: "/images/color-correction/color-correction-showcase-pattern-change-dress-after.webp",
    alt: "Black floral dress after the pattern change",
    width: 400,
    height: 500,
  },
  gownRecolorBefore: {
    src: "/images/color-correction/color-correction-product-chloe-gown-before.webp",
    alt: "Fashion gown in original magenta colorway before colorway recoloring",
    width: 1500,
    height: 1875,
  },
  gownRecolorAfter: {
    src: "/images/color-correction/color-correction-product-chloe-gown-after.webp",
    alt: "Fashion gown recolored to black colorway with authentic sheen, shadow depth, and fabric folds preserved",
    width: 1500,
    height: 1875,
  },
  hdrInteriorBefore: {
    src: "/images/color-correction/color-correction-comparison-hdr-interior-shepparson-before.webp",
    alt: "Architectural interior raw photo with mixed color temperatures and dark window shadows before HDR color correction",
    width: 1600,
    height: 2000,
  },
  hdrInteriorAfter: {
    src: "/images/color-correction/color-correction-comparison-hdr-interior-shepparson-after.webp",
    alt: "Finished architectural interior with balanced HDR exposure, neutral white balance, and rich ambient warmth",
    width: 1600,
    height: 2000,
  },
} as const;

export const colorCorrectionPage: ServicePageData = {
  slug: "color-correction",
  seo: {
    title: "Product Photo Color Correction | Studio Click House",
    description:
      "Product photo color correction for e-commerce, catalogs, campaigns, and brand teams. Match white balance, exposure, skin tones, and product color across a series.",
  },
  hero: {
    title: "Color",
    titleAccent: "Correction.",
    theme: "light",
    titleWidth: "wide",
    description:
      "Correct white balance, exposure, and product color so a complete image set looks consistent across screens, marketplaces, and print.",
    aspectRatio: "4/5",
    media: colorCorrectionMedia.heroSunglasses,
    supportingMedia: [
      colorCorrectionMedia.heroFashionTop1,
      colorCorrectionMedia.heroFashionTop2,
    ],
  },
  intro: {
    heading: "What is color correction?",
    paragraphs: [
      "Color correction sets a reliable base for a consistent image series. We adjust white balance, exposure, black and white points, contrast, and saturation for product, fashion, and campaign work.",
    ],
    stages: [
      {
        label: "Neutralize the source",
        description:
          "Correct white balance and exposure so the image begins from a clean, dependable base.",
        media: colorCorrectionMedia.introFashionBoard,
      },
      {
        label: "Match the series",
        description:
          "Align hue, contrast, and product color across every frame in the collection.",
      },
      {
        label: "Finish for output",
        description:
          "Refine saturation and tonal range for the final marketplace, campaign, or screen.",
      },
    ],
  },
  beforeAfter: {
    heading: "Product color correction & color change",
    description:
      "Compare the original cosmetic image with its color-corrected finish, refining product tone while keeping the cream and packaging details clear.",
    bullets: [
      "Match product hues to supplied color references",
      "Correct color casts and uneven lighting",
      "Keep tones consistent across image sets",
    ],
    beforeSrc: colorCorrectionMedia.comparisonOriginal.src,
    beforeAlt: colorCorrectionMedia.comparisonOriginal.alt,
    afterSrc: colorCorrectionMedia.comparisonCorrected.src,
    afterAlt: colorCorrectionMedia.comparisonCorrected.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: colorCorrectionMedia.comparisonCorrected.width,
    height: colorCorrectionMedia.comparisonCorrected.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "Pattern change retouching",
    theme: "light",
    description:
      "Change a garment's pattern while keeping its shape, folds, and fabric detail consistent in the finished image.",
    bullets: [
      "Replace or refine garment patterns",
      "Preserve fabric folds and garment details",
      "Keep edits consistent across a product range",
    ],
    beforeAfter: {
      before: { ...colorCorrectionMedia.patternChangeBefore, label: "Before" },
      after: { ...colorCorrectionMedia.patternChangeAfter, label: "After" },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "color-correction-gown-colorway-comparison",
      heading: "Garment recoloring & colorway conversion",
      description:
        "Transform apparel into alternate seasonal colorways—such as shifting from vibrant hues to deep blacks—while preserving natural sheen, fabric folds, texture, and ambient shadows.",
      bullets: [
        "Generate multi-colorway e-commerce assets from a single photoshoot",
        "Retain authentic satin sheen, highlights, and shadow gradients",
        "Calibrated color accuracy tailored for apparel lookbooks and catalogs",
      ],
      beforeSrc: colorCorrectionMedia.gownRecolorBefore.src,
      beforeAlt: colorCorrectionMedia.gownRecolorBefore.alt,
      afterSrc: colorCorrectionMedia.gownRecolorAfter.src,
      afterAlt: colorCorrectionMedia.gownRecolorAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: colorCorrectionMedia.gownRecolorAfter.width,
      height: colorCorrectionMedia.gownRecolorAfter.height,
      layout: "cards",
      textPosition: "right",
    },
    {
      sectionId: "color-correction-hdr-interior-comparison",
      heading: "HDR interior & architectural color correction",
      description:
        "Balance multi-source ambient lighting, window exposures, and mixed color temperatures to produce crisp, true-to-life architectural and real estate visuals.",
      bullets: [
        "Neutralize mixed indoor tungsten and daylight color casts",
        "Recover deep shadow details and dynamic window highlights",
        "Harmonize wood tones, wall textures, and natural ambient light",
      ],
      beforeSrc: colorCorrectionMedia.hdrInteriorBefore.src,
      beforeAlt: colorCorrectionMedia.hdrInteriorBefore.alt,
      afterSrc: colorCorrectionMedia.hdrInteriorAfter.src,
      afterAlt: colorCorrectionMedia.hdrInteriorAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: colorCorrectionMedia.hdrInteriorAfter.width,
      height: colorCorrectionMedia.hdrInteriorAfter.height,
      layout: "cards",
      textPosition: "left",
    },
  ],
  productTypes: [
    {
      id: "editorial-prints-after",
      code: "01",
      title: "Editorial Print (Corrected)",
      subtitle: "Calibrated blue canal print with balanced exposure and natural fabric drape.",
      src: "/images/color-correction/color-correction-hero-garment-editorial-after.webp",
      alt: "Editorial garment after color correction with a blue canal print",
      fit: "cover",
    },
    {
      id: "editorial-prints-before",
      code: "02",
      title: "Editorial Print (Source)",
      subtitle: "Original capture before color correction, exposure alignment, and tonal balance.",
      src: "/images/color-correction/color-correction-hero-garment-editorial-before.webp",
      alt: "Editorial garment before color correction with purple canal print",
      fit: "cover",
    },
    {
      id: "tailored-trousers",
      code: "03",
      title: "Pattern & Textile Balancing",
      subtitle: "Harmonize multi-color illustrative patterns and base fabric undertones.",
      src: "/images/color-correction/color-correction-hero-pattern-change-trousers.webp",
      alt: "Printed tailored trousers with accurate color matching",
      fit: "cover",
    },
    {
      id: "garment-colorway-01",
      code: "04",
      title: "Colorway Variant — 01",
      subtitle: "Precision shade shifting preserving highlights, fabric weave, and depth.",
      src: "/images/color-correction/color-correction-grid-garment-colorway-01.webp",
      alt: "Apparel garment colorway conversion variant 01",
      fit: "cover",
    },
    {
      id: "garment-colorway-02",
      code: "05",
      title: "Colorway Variant — 02",
      subtitle: "True-to-swatch palette matching across multi-garment catalog lines.",
      src: "/images/color-correction/color-correction-grid-garment-colorway-02.webp",
      alt: "Apparel garment colorway conversion variant 02",
      fit: "cover",
    },
    {
      id: "garment-colorway-03",
      code: "06",
      title: "Colorway Variant — 03",
      subtitle: "Consistent ambient shadows, seams, and metal trim calibration.",
      src: "/images/color-correction/color-correction-grid-garment-colorway-03.webp",
      alt: "Apparel garment colorway conversion variant 03",
      fit: "cover",
    },
  ],
  features: {
    heading: "Product photo color correction workflow",
    items: [
      {
        icon: "ShieldCheck",
        title: "Three-Tier Quality Control",
        description:
          "Every file passes through dedicated colorist review, lead artist verification, and technical calibration checks before final handoff.",
      },
      {
        icon: "Pipette",
        title: "Spectrophotometer & Swatch Matching",
        description:
          "We match exact Pantone codes, physical fabric swatches, and LAB/RGB values to guarantee 100% true-to-life product fidelity.",
      },
      {
        icon: "Layers",
        title: "Multi-Colorway Batch Expansion",
        description:
          "Turn a single product capture into an entire seasonal catalog of coordinated colorways, saving substantial photography costs.",
      },
      {
        icon: "Palette",
        title: "Calibrated 16-Bit Color Pipelines",
        description:
          "Workflows in Display P3, Adobe RGB, and CMYK press profiles with EIZO ColorEdge proofing prevent banding and color shifts.",
      },
      {
        icon: "Zap",
        title: "Swift Overnight Turnaround",
        description:
          "Our 24/7 multi-shift color suite guarantees rapid 12 to 24-hour turnaround for high-volume e-commerce and catalog releases.",
      },
      {
        icon: "Headphones",
        title: "24/7 Dedicated Production Support",
        description:
          "Direct real-time communication with our production team for custom color profiles, urgent volume batches, and instant revisions.",
      },
    ],
  },
  faqs: [
    {
      question:
        "What does the Color Correction service entail at Studio Click House?",
      answer:
        "Our Color Correction service involves fine-tuning the colors in your images to enhance their vibrancy, consistency, and overall appeal. We adjust hues, saturation, and balance to ensure your visuals align perfectly with your brand's aesthetic.",
    },
    {
      question:
        "How is the pricing structured for the Color Correction service?",
      answer:
        "Pricing is competitive and customized based on the specific needs of each project. We also provide discounts for bulk orders to offer cost-effective solutions for our clients.",
    },
    {
      question:
        "What is the usual turnaround time for the Color Correction service?",
      answer:
        "We prioritize quick delivery without compromising quality. Turnaround times are tailored to meet your scheduling needs, ensuring your projects stay on track.",
    },
    {
      question:
        "How does Studio Click House ensure the quality of its Color Correction service?",
      answer:
        "Every image goes through a three-step review for color, consistency, and export settings before delivery.",
    },
    {
      question:
        "Is there a satisfaction guarantee for the Color Correction service?",
      answer:
        "Yes, we offer a 100% money-back guarantee if you are not fully satisfied with our service. Ensuring your satisfaction is our utmost priority.",
    },
  ],
  cta: {
    heading: "Need high-quality color correction?",
    description:
      "Studio Click House is one of the best Color Correction service providers. We deliver professional color correction at an affordable cost. Send us sample files and we will prepare a quote according to your requirements.",
    steps: [
      {
        title: "Send sample files",
        description:
          "Share your images, references, and delivery requirements.",
      },
      {
        title: "Review a test edit",
        description:
          "Approve the color correction style before full production.",
      },
      {
        title: "Receive final delivery",
        description: "Assets pass through our three-step QC before handoff.",
      },
    ],
  },
};
